import type { OverDetails } from "@geoblocks/elevation-profile";
import ElevationProfileElement from "@geoblocks/elevation-profile";
import type { TemplateResult } from "lit-html";
import { svg } from "lit-html";
import { distance as getDistance } from "ol/coordinate";
import Feature from "ol/Feature";
import type LineString from "ol/geom/LineString";
import Point from "ol/geom/Point";
import VectorLayer from "ol/layer/Vector";
import type OLMap from "ol/Map";
import VectorSource from "ol/source/Vector";
import { indexToLabel } from "./utils";

export type HikingProfile = {
  lines: number[][][];
  points: number[][];
};

export type ElevationProfileOptions = {
  /**
   * The target element for the elevation profile
   */
  target: HTMLElement | string;
  /**
   * An OpenLayers map instance
   */
  map: OLMap;
};

/**
 * # ElevationProfile
 *
 * ## Demo
 * - [ElevationProfile](/demo/elevation-profile.html)
 *
 * ## Description
 *
 * The ElevationProfile is responsible for displaying the elevation profile of a hiking and its stopovers.
 *
 * To use the ElevationProfile, you need to set the target, the element where to display to profile, and the map.
 * ```js
 * const profile = new sww.ElevationProfile({
 *   target: "profile",
 *   map: map,
 * });
 * ```
 * Where `profile` is the id of the element where to display the profile and `map` is an OpenLayers map instance.
 *
 * Then, the profile needs to be linked to the HikingEditor to the hiking editor to be updated when the hiking is changes:
 * ```js
 * const editor = new sww.HikingEditor();
 *
 * editor.addEventListener("change", () => {
 *   const { lines, points } = editor.getProfile();
 *   profile.setProfile(lines, points);
 * });
 * ```
 *
 * ## Styling
 *
 * The `sww.css` file must be included in the page to style the elevation profile.
 *
 */
export default class ElevationProfile {
  private profile: ElevationProfileElement;
  private highlightPoint: Point;
  private highlightLayer: VectorLayer<Feature>;
  private yRange: [number, number] | null = null;
  private yBounds: [number, number] | null = null;

  constructor(options: ElevationProfileOptions) {
    this.profile = new ElevationProfileElement();

    const target = typeof options.target === "string" ? document.getElementById(options.target) : options.target;

    if (!target) {
      throw new Error("ElevationProfile: target not found");
    }
    if (!options.map) {
      throw new Error("ElevationProfile: map not found");
    }

    this.highlightPoint = new Point([]);
    this.highlightLayer = new VectorLayer<Feature>({
      style: {
        "circle-radius": 8,
        "circle-fill-color": "#F2000C",
        "circle-stroke-color": "#fff",
        "circle-stroke-width": 3,
      },
      source: new VectorSource<Feature>({
        features: [new Feature(this.highlightPoint)],
      }),
    });
    this.highlightLayer.setMap(options.map);

    this.profile.tickFormat = this.tickFormat.bind(this);
    this.profile.pointSvg = this.pointSvg.bind(this);

    this.profile.updateScale = this.updateScale.bind(this);

    this.profile.addEventListener("over", this.onElevationProfileOver.bind(this) as EventListener);
    this.profile.addEventListener("out", this.onElevationProfileOut.bind(this));

    this.profile.margin = { top: 20, right: 40, bottom: 40, left: 60 };
    target.appendChild(this.profile);
  }

  public async setProfile(lines: number[][][], points: number[][]) {
    const elevations = lines.flat().map((line) => line[2]);
    this.yRange = [Math.min(...elevations), Math.max(...elevations)];
    this.profile.lines = lines;
    this.profile.points = points;
    await this.profile.updateComplete;
    this.hideNotUsedAxisPart();
  }

  private tickFormat(value: number, _axis: "x" | "y"): string {
    return value.toString();
  }

  getTickValues(axisHeight: number, yDomainLength: number, yTickSize = 40): number[] {
    if (!this.yRange?.length) return [];
    const numTicks = Math.ceil(axisHeight / yTickSize);
    const [min, max] = this.yRange;
    const values: number[] = [];

    const yMean = (max + min) / 2;
    const yHalfDomain = yDomainLength / 2;
    let rawLowerBound = yMean - yHalfDomain;
    let rawUpperBound = yMean + yHalfDomain;

    // Shift domain if lower bound is negative
    if (rawLowerBound < 0) {
      const shift = 0 - rawLowerBound;
      rawLowerBound = 0;
      rawUpperBound += shift;
    }

    const rawBoundsRange = rawUpperBound - rawLowerBound;
    const rawStep = rawBoundsRange / numTicks;
    const magnitude = 10 ** Math.floor(Math.log10(rawStep));
    const residual = rawStep / magnitude;
    let step = 10;
    if (residual < 1.5) step = 1;
    else if (residual < 3) step = 2;
    else if (residual < 7) step = 5;
    step *= magnitude;

    let lowerBound = step * Math.round(rawLowerBound / step);
    let upperBound = Math.round(rawUpperBound + (lowerBound - rawLowerBound));

    // Shift domain if lowest point is out of plot
    if (min < lowerBound) {
      const shift = step * Math.ceil((lowerBound - min) / step);
      lowerBound -= shift;
      upperBound -= shift;
    }

    for (let i = 0; i <= numTicks + 1; i++) {
      let value = lowerBound + i * step;
      if (step < 5) {
        const decimalPart = value % 5;
        value = decimalPart >= 2.5 ? Math.ceil(value / 5) * 5 : Math.floor(value / 5) * 5;
      }

      if (value <= upperBound) {
        values.push(value);
      } else {
        // Ensure the last value does not exceed the max
        values.push(upperBound);
        break;
      }
    }

    if (values.length > 1 && values[values.length - 1] > upperBound) {
      values[values.length - 1] = upperBound;
    }

    // Reduce plot size by removing ticks that far from the min point for more than one step
    if (min - lowerBound > step) {
      const newLowerBound = values.reduce((closest, num) => {
        return num < min && min - num < min - closest ? num : closest;
      }, lowerBound);

      const shift = newLowerBound - lowerBound;
      values[0] = newLowerBound;
      for (let i = 1; i < values.length; i++) {
        values[i] += shift;
      }
    }

    return values;
  }

  private pointSvg(x: number, y: number, index: number): TemplateResult {
    const points = this.profile.points[index];
    const first = index === 0;
    const last = index === this.profile.points.length - 1;
    const profileBottom = this.profile.offsetHeight - this.profile.margin.bottom;
    const axisLabel = (points[3] / 1000).toFixed(1) + (last ? " km" : "");

    let circleCenter: TemplateResult;

    if (first || last) {
      circleCenter = svg`<circle class="${`stopover-${first ? "first" : "last"}`}" cx="${x}" cy="${y}" r="8" />`;
    } else {
      circleCenter = svg`<text class="stopover-text" x="${x}" y="${y}" dy=".4em">${indexToLabel(index)}</text>`;
    }

    return svg`
      <g>
        <line class="stopover-line" x1="${x}" y1="${y}" x2="${x}" y2="${profileBottom + 6}"/>
        <circle class="stopover-circle" cx="${x}" cy="${y}" r="12" />
        ${circleCenter}
        <text class="stopover-axis" x="${x}" y="${profileBottom + 24}" dy=".3em">${axisLabel}</text>
      </g>
    `;
  }

  private onElevationProfileOver(event: CustomEvent<OverDetails>) {
    this.highlightPoint.setCoordinates(event.detail.coordinate);
  }

  private onElevationProfileOut() {
    this.highlightPoint.setCoordinates([]);
  }

  private updateScale(
    scaleX: d3.ScaleLinear<number, number>,
    scaleY: d3.ScaleLinear<number, number>,
    _width: number,
    height: number,
  ) {
    const ratioXY = 0.5;

    const xDomain = scaleX.domain();

    const xMin = xDomain[0];
    const xMax = xDomain[1];

    const yDomain = scaleY.domain();
    const yMax = yDomain[1];

    const margin = this.profile.margin;
    const axisHeight = height - margin.top - margin.bottom;
    let yDomainLength: number | undefined;
    let tickValues: number[] | undefined;

    const width_scales = ["100%", "90%", "80%", "70%", "60%", "50%", "40%", "30%", "20%", "10%"];
    for (const width_scale of width_scales) {
      this.profile.style.width = width_scale;
      const axisWidth = this.profile.offsetWidth - margin.left - margin.right;
      const xResolution = (xMax - xMin) / axisWidth;
      yDomainLength = ratioXY * xResolution * axisHeight;

      tickValues = this.getTickValues(axisHeight, yDomainLength);
      const step = tickValues[1] - tickValues[0];
      const lastTick = tickValues[tickValues.length - 1];
      const secondLastTickValue = tickValues[tickValues.length - 2];
      const lastTickIsNice = lastTick % step === 0;

      // To have nice ticks, either the last tick is multiple of the step
      // or the second last tick (that is always nice)is greater than the max, in which case the last tick will be hidden afterward
      if ((lastTickIsNice && lastTick > yMax) || secondLastTickValue > yMax) break;
    }

    // set y domain and tick values
    if (yDomainLength && tickValues) {
      this.yBounds = [tickValues[0], tickValues[tickValues.length - 1]];
      scaleY.domain(this.yBounds);
      this.profile.tickValues(tickValues, "y");
    }
  }

  hideNotUsedAxisPart() {
    const yAxis = this.profile.querySelector(".y.axis");
    if (yAxis && this.yRange?.length) {
      const yMax = this.yRange[1];
      let lastTickValue: number | undefined;
      const ticks = yAxis.querySelectorAll(".tick");
      for (let i = 0; i < ticks.length; i++) {
        const tick = ticks[i];
        const value = Number(tick.querySelector("text")?.textContent);
        const tickStyle = (<HTMLElement>tick).style;
        if (yMax < value && lastTickValue && lastTickValue !== value) {
          tickStyle.display = "none";
        } else if (yMax < value) {
          lastTickValue = value;
          tickStyle.display = "block";
        }
      }

      if (lastTickValue && this.yBounds) {
        const yLowerBound = this.yBounds[0];
        const yUpperBound = this.yBounds[1];
        const scale = (lastTickValue - yLowerBound) / (yUpperBound - yLowerBound);
        const domain = <SVGAElement>yAxis.querySelector(".domain");
        const bbox = domain.getBBox();
        domain.style.transformOrigin = `50% ${bbox.y + bbox.height}px`;
        domain.setAttribute("transform", `scale(1,${scale})`);
      }
    }
  }
}

export function getProfile(segments: Feature<LineString>[][], stopoverIndexes: number[][]): HikingProfile {
  // remove empty segments
  const cleanSegments = segments.filter((s) => s.length > 0);

  // Remove corresponding stopover indexes
  const emptySegmentsIndexes = segments.map((s, i) => (s.length === 0 ? i : -1)).filter((i) => i !== -1);
  const cleanStopoverIndexes = stopoverIndexes.filter((index) => !emptySegmentsIndexes.includes(index[0]));

  // renumber stopover parts to match clean segments
  let currentPartIndex = -1;
  let prevPartIndex = -1;
  for (let i = 0; i < cleanStopoverIndexes.length; i++) {
    if (cleanStopoverIndexes[i][0] !== prevPartIndex) {
      currentPartIndex++;
    }
    prevPartIndex = cleanStopoverIndexes[i][0];
    cleanStopoverIndexes[i][0] = currentPartIndex;
  }

  const lines = [];
  const points = [];
  let distance = 0;
  let prevEndPoint = null;
  for (const part of cleanSegments) {
    if (prevEndPoint) {
      // @ts-expect-error: we remove empty segments beforehand
      const nextFirstPoint = part[0].getGeometry().getFirstCoordinate();
      distance += getDistance(prevEndPoint, nextFirstPoint);
    }
    const line = [];
    for (const segment of part) {
      const profile = segment.get("profile").map((c: number[]) => [c[0], c[1], c[2], c[3] + distance]);
      points.push(profile[0]);
      line.push(...profile);
      distance = profile.at(-1).at(3);
    }
    prevEndPoint = line.at(-1);
    points.push(prevEndPoint);
    lines.push(line);
  }

  const stopovers = [];
  const pointsByPart = cleanSegments.map((s) => s.length + 1);
  for (const [part, index] of cleanStopoverIndexes) {
    const pointIndex = pointsByPart.slice(0, part).reduce((acc, curr) => acc + curr, 0) + index;
    const point = points[pointIndex];
    if (point) {
      stopovers.push(points[pointIndex]);
    }
  }

  return {
    lines: lines,
    points: stopovers,
  };
}
